/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled from "styled-components";
import { ArrowUpRight } from "lucide-react";

import MigrationFlow from "@src/components/modules/LiveModule/MigrationFlow";
import PhaseStepper from "@src/components/modules/LiveModule/PhaseStepper";
import DiskRows from "@src/components/modules/LiveModule/DiskRows";
import Sparkline from "@src/components/modules/LiveModule/Sparkline";
import { Bar, Chip, Label, TextLink } from "@src/components/modules/LiveModule/ui/LiveUi";
import { LIVE, formatClock, phaseTone, targetFill } from "@src/components/modules/LiveModule/liveUtils";
import type { Task } from "@src/@types/Task";
import {
  KnownDisk,
  formatBytes,
  formatDuration,
  formatRate,
  isActiveStatus,
  summariseExecution,
} from "@src/utils/TransferProgress";

/*
 * Live progress of one running execution (or deployment), for the transfer Executions view and the
 * deployment task view: a small MigrationFlow, the phase, the overall bar, throughput with a
 * sparkline of the last few minutes, time left and when it should finish, and a row per disk.
 * It re-reads the tasks it is given on every poll; the samples for the sparkline stay in memory.
 */

const Wrapper = styled.section`
  background: var(--t9-bg-surface);
  border: 1px solid ${LIVE.border};
  border-radius: 8px;
  margin-top: 16px;
  overflow: hidden;
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid ${LIVE.border};
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${LIVE.text};
    flex-grow: 1;
  }
`;
const StepperRow = styled.div`
  padding: 14px 20px 0 20px;
`;
const Main = styled.div`
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr) minmax(0, 320px);
  gap: 24px;
  align-items: center;
  padding: 8px 20px 16px 20px;
  @media (max-width: 1100px) {
    grid-template-columns: 240px minmax(0, 1fr);
    & > :last-child {
      grid-column: 1 / -1;
    }
  }
`;
const Middle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;
const Pct = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  .n {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${LIVE.text};
    font-variant-numeric: tabular-nums;
  }
  .s {
    font-size: 13px;
    color: ${LIVE.muted};
    b {
      color: ${LIVE.text};
      font-weight: 600;
    }
  }
`;
const Side = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
  min-width: 0;
  .v {
    font-size: 16px;
    font-weight: 700;
    color: ${LIVE.text};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .s {
    font-size: 12px;
    color: ${LIVE.muted};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const Disks = styled.div`
  padding: 0 20px 14px 20px;
`;

type Sample = { t: number; rate: number };

type Props = {
  tasks: Task[];
  status: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  executionType?: string;
  sourceType?: string | null;
  targetType?: string | null;
  knownDisks?: { [instance: string]: KnownDisk[] };
  title?: string;
  /** Show it for finished runs too (off: renders nothing unless the run is active). */
  showWhenFinished?: boolean;
  /** Keeps the sparkline samples apart per run. */
  sampleKey?: string;
};

const samplesByKey = new Map<string, Sample[]>();

const LiveProgress = (props: Props) => {
  const [now, setNow] = React.useState(Date.now());
  const active = isActiveStatus(props.status);
  React.useEffect(() => {
    if (!active) {
      return undefined;
    }
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);

  const progress = React.useMemo(
    () =>
      summariseExecution(
        {
          status: props.status,
          tasks: props.tasks,
          created_at: props.createdAt,
          updated_at: props.updatedAt,
          type: props.executionType,
        },
        { knownDisks: props.knownDisks },
      ),
    [props.status, props.tasks, props.createdAt, props.updatedAt, props.executionType, props.knownDisks],
  );

  const key = props.sampleKey || "default";
  const [samples, setSamples] = React.useState<Sample[]>(() => samplesByKey.get(key) || []);
  React.useEffect(() => {
    if (!active) {
      return;
    }
    const prev = samplesByKey.get(key) || [];
    const next = [...prev, { t: Date.now(), rate: progress.rateBytesPerSec }].slice(-36);
    samplesByKey.set(key, next);
    setSamples(next);
  }, [progress, key, active]);

  if (!active && !props.showWhenFinished) {
    return null;
  }
  const p = progress;
  const finishAt = active && p.etaSeconds != null ? now + p.etaSeconds * 1000 : null;
  const elapsed = active && p.startedAt ? (now - p.startedAt) / 1000 : p.elapsedSeconds;

  return (
    <Wrapper aria-label="Live progress" data-test-id="liveProgress">
      <Head>
        <h3>{props.title || "Live progress"}</h3>
        <Chip tone={phaseTone(p.phase)} pulse={active && p.phase === "Syncing disks"}>
          {p.phase}
        </Chip>
        <TextLink to="/live">
          Live monitor <ArrowUpRight size={14} />
        </TextLink>
      </Head>
      <StepperRow>
        <PhaseStepper phases={p.phases} phase={p.phase} phaseIndex={p.phaseIndex} compact />
      </StepperRow>
      <Main>
        <MigrationFlow
          size="small"
          sourceType={props.sourceType}
          targetType={props.targetType}
          phase={p.phase}
          percent={targetFill(p)}
          rateBytesPerSec={active ? p.rateBytesPerSec : 0}
          activeDisks={p.activeDisks}
        />
        <Middle>
          <div>
            <Pct>
              <span className="n">{Math.floor(p.percent)}%</span>
              <span className="s">
                {p.hasDiskData ? (
                  <>
                    <b>{formatBytes(p.doneBytes)}</b> of {formatBytes(p.totalBytes)} copied
                  </>
                ) : (
                  <>
                    <b>{p.tasksDone}</b> of {p.tasksTotal} steps done
                  </>
                )}
              </span>
            </Pct>
            <div style={{ marginTop: 6 }}>
              <Bar
                percent={p.percent}
                height={8}
                moving={active && p.rateBytesPerSec > 0}
                tone={p.phase === "Completed" ? "success" : p.phase === "Failed" ? "danger" : "info"}
                label="Overall progress"
              />
            </div>
          </div>
        </Middle>
        <Side>
          <div>
            <Label>Throughput</Label>
            <div className="v">{active && p.rateBytesPerSec > 0 ? formatRate(p.rateBytesPerSec) : "—"}</div>
            <div style={{ marginTop: 4 }}>
              <Sparkline values={samples.length ? samples.map(s => s.rate) : [0]} height={22} min={0} />
            </div>
          </div>
          <div>
            <Label>Time left</Label>
            <div className="v">
              {!active ? (p.phase === "Completed" ? "Done" : "—") : p.etaSeconds != null ? `~${formatDuration(p.etaSeconds)}` : "Estimating…"}
            </div>
            <div className="s">{finishAt ? `Finishes about ${formatClock(finishAt, now)}` : " "}</div>
          </div>
          <div>
            <Label>Elapsed</Label>
            <div className="v">{formatDuration(elapsed)}</div>
            <div className="s">Started {formatClock(p.startedAt, now)}</div>
          </div>
          <div>
            <Label>Disks</Label>
            <div className="v">
              {p.disks.length ? `${p.completedDisks} of ${p.disks.filter(d => !d.scanning).length} done` : "—"}
            </div>
            <div className="s">{p.activeDisks ? `${p.activeDisks} copying now` : " "}</div>
          </div>
        </Side>
      </Main>
      {p.disks.length ? (
        <Disks>
          <DiskRows disks={p.disks} showInstance={p.instances.length > 1} />
        </Disks>
      ) : null}
    </Wrapper>
  );
};

export default LiveProgress;
